import React, { useState, useEffect } from 'react';
import { getStatusColor, getPriorityColor, formatDate, getTimeAgo } from '../../utils/helpers';
import { getAvailableAgents, reassignTicket, uploadAttachment, getRelatedTickets } from '../../api/ticketApi';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './TicketDetail.css';

export default function TicketDetail({ ticket, onUpdate, onAddComment, canEdit = true }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Form states
  const [formData, setFormData] = useState({
    status: ticket?.status || 'Open',
    priority: ticket?.priority || 'Medium',
    resolution: ticket?.resolution || ''
  });
  
  // Comment states
  const [commentData, setCommentData] = useState({
    message: '',
    file: null
  });
  
  // UI states
  const [uiState, setUiState] = useState({
    isUpdating: false,
    isUploadingFile: false,
    showReassignModal: false,
    showRelatedTickets: false,
    activeTab: 'activity' // activity, stats, related
  });
  
  // Reassignment states
  const [reassignData, setReassignData] = useState({
    agents: [],
    selectedAgentId: '',
    isLoading: false
  });
  
  // Related tickets states
  const [relatedTicketsData, setRelatedTicketsData] = useState({
    tickets: [],
    isLoading: false
  });

  // Permissions
  const permissions = {
    canReassign: user?.role === 'manager' || user?.role === 'team_lead',
    isManager: user?.role === 'manager',
    isTeamLead: user?.role === 'team_lead'
  };

  // Calculate ticket stats
  const ticketStats = {
    responseTime: ticket?.logs?.length > 0 
      ? getTimeAgo(ticket.logs[0].created_at)
      : 'No responses yet',
    totalResponses: ticket?.logs?.length || 0,
    lastActivity: ticket?.logs?.length > 0
      ? getTimeAgo(ticket.logs[ticket.logs.length - 1].created_at)
      : 'No activity',
    attachmentCount: ticket?.logs?.filter(log => log.attachment).length || 0,
    timeOpen: getTimeAgo(ticket?.created_at || new Date())
  };

  useEffect(() => {
    if (ticket) {
      setFormData({
        status: ticket.status,
        priority: ticket.priority,
        resolution: ticket.resolution || ''
      });
    }
  }, [ticket]);

  const loadAvailableAgents = async () => {
    if (!permissions.canReassign) return;
    setReassignData(prev => ({ ...prev, isLoading: true }));
    
    try {
      const response = await getAvailableAgents(user?.role);
      const agents = user?.role === 'manager' ? response.agents : response.team_members;
      setReassignData(prev => ({ 
        ...prev, 
        agents: agents || [],
        isLoading: false 
      }));
    } catch (error) {
      console.error('Error loading agents:', error);
      setReassignData(prev => ({ ...prev, isLoading: false }));
    }
  };

  const loadRelatedTickets = async () => {
    setRelatedTicketsData(prev => ({ ...prev, isLoading: true }));
    
    try {
      const response = await getRelatedTickets(ticket.id);
      setRelatedTicketsData({
        tickets: response.tickets || [],
        isLoading: false
      });
    } catch (error) {
      console.error('Error loading related tickets:', error);
      setRelatedTicketsData({ tickets: [], isLoading: false });
    }
  };

  const handleReassignTicket = async () => {
    if (!reassignData.selectedAgentId) {
      alert('Please select an agent');
      return;
    }
    
    try {
      await reassignTicket(ticket.id, reassignData.selectedAgentId, user?.role);
      alert('✅ Ticket reassigned successfully!');
      setUiState(prev => ({ ...prev, showReassignModal: false }));
      window.location.reload();
    } catch (error) {
      alert(error.response?.data?.msg || '❌ Failed to reassign ticket');
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const maxSize = 16 * 1024 * 1024;
      
      if (file.size > maxSize) {
        alert('File size exceeds 16MB limit');
        return;
      }
      
      setCommentData(prev => ({ ...prev, file }));
    }
  };

  const handleUpdateTicket = async () => {
    setUiState(prev => ({ ...prev, isUpdating: true }));
    
    try {
      await onUpdate(formData, user?.role);
    } finally {
      setUiState(prev => ({ ...prev, isUpdating: false }));
    }
  };

  const handleAddCommentSubmit = async () => {
    if (!commentData.message.trim()) {
      alert('Please enter a comment');
      return;
    }
    
    let attachmentUrl = null;
    
    if (commentData.file) {
      setUiState(prev => ({ ...prev, isUploadingFile: true }));
      
      try {
        const uploadResponse = await uploadAttachment(commentData.file);
        attachmentUrl = uploadResponse.url;
      } catch (error) {
        alert('❌ Failed to upload attachment');
        setUiState(prev => ({ ...prev, isUploadingFile: false }));
        return;
      }
    }
    
    setUiState(prev => ({ ...prev, isUploadingFile: false }));
    await onAddComment(commentData.message, attachmentUrl, user?.role);
    
    setCommentData({ message: '', file: null });
    document.querySelector('.ticket-detail__file-input').value = '';
  };

  const toggleReassignModal = () => {
    const newState = !uiState.showReassignModal;
    setUiState(prev => ({ ...prev, showReassignModal: newState }));
    
    if (newState && reassignData.agents.length === 0) {
      loadAvailableAgents();
    }
  };

  if (!ticket) {
    return (
      <div className="ticket-detail__loading">
        <div className="spinner"></div>
        <p>Loading ticket details...</p>
      </div>
    );
  }

  return (
    <div className="ticket-detail">
      {/* Hero Header */}
      <div className="ticket-detail__hero">
        <div className="ticket-detail__hero-content">
          <div className="ticket-detail__hero-left">
            <span className="ticket-detail__hero-label">Ticket</span>
            <h1 className="ticket-detail__hero-title">{ticket.ticket_number}</h1>
            <div className="ticket-detail__hero-badges">
              <span
                className="ticket-detail__hero-badge"
                style={{ backgroundColor: getStatusColor(ticket.status) }}
              >
                {ticket.status}
              </span>
              <span
                className="ticket-detail__hero-badge"
                style={{ backgroundColor: getPriorityColor(ticket.priority) }}
              >
                🔥 {ticket.priority}
              </span>
            </div>
          </div>
          
          <div className="ticket-detail__hero-actions">
            {permissions.canReassign && (
              <button
                className="ticket-detail__hero-btn ticket-detail__hero-btn--reassign"
                onClick={toggleReassignModal}
              >
                <span>🔄</span> Reassign
              </button>
            )}
            
            <button
              className="ticket-detail__hero-btn ticket-detail__hero-btn--related"
              onClick={() => {
                setUiState(prev => ({ ...prev, activeTab: 'related' }));
                if (relatedTicketsData.tickets.length === 0) {
                  loadRelatedTickets();
                }
              }}
            >
              <span>📋</span> Related ({relatedTicketsData.tickets.length})
            </button>
          </div>
        </div>
        
        {/* Stats Bar */}
        <div className="ticket-detail__stats-bar">
          <div className="ticket-detail__stat">
            <span className="ticket-detail__stat-icon">⏱️</span>
            <div className="ticket-detail__stat-content">
              <span className="ticket-detail__stat-label">Time Open</span>
              <span className="ticket-detail__stat-value">{ticketStats.timeOpen}</span>
            </div>
          </div>
          
          <div className="ticket-detail__stat">
            <span className="ticket-detail__stat-icon">💬</span>
            <div className="ticket-detail__stat-content">
              <span className="ticket-detail__stat-label">Responses</span>
              <span className="ticket-detail__stat-value">{ticketStats.totalResponses}</span>
            </div>
          </div>
          
          <div className="ticket-detail__stat">
            <span className="ticket-detail__stat-icon">📎</span>
            <div className="ticket-detail__stat-content">
              <span className="ticket-detail__stat-label">Attachments</span>
              <span className="ticket-detail__stat-value">{ticketStats.attachmentCount}</span>
            </div>
          </div>
          
          <div className="ticket-detail__stat">
            <span className="ticket-detail__stat-icon">🕐</span>
            <div className="ticket-detail__stat-content">
              <span className="ticket-detail__stat-label">Last Activity</span>
              <span className="ticket-detail__stat-value">{ticketStats.lastActivity}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Reassignment Modal */}
      {uiState.showReassignModal && permissions.canReassign && (
        <div className="ticket-detail__modal-overlay" onClick={toggleReassignModal}>
          <div className="ticket-detail__modal" onClick={(e) => e.stopPropagation()}>
            <div className="ticket-detail__modal-header">
              <h3>🔄 Reassign Ticket</h3>
              <button className="ticket-detail__modal-close" onClick={toggleReassignModal}>×</button>
            </div>
            
            <div className="ticket-detail__modal-body">
              {reassignData.isLoading ? (
                <div className="ticket-detail__modal-loading">
                  <div className="spinner"></div>
                  <p>Loading agents...</p>
                </div>
              ) : (
                <>
                  <div className="ticket-detail__form-group">
                    <label className="ticket-detail__label">Select Agent</label>
                    <select
                      className="ticket-detail__select ticket-detail__select--large"
                      value={reassignData.selectedAgentId}
                      onChange={(e) => setReassignData(prev => ({ 
                        ...prev, 
                        selectedAgentId: e.target.value 
                      }))}
                    >
                      <option value="">Choose an agent...</option>
                      {reassignData.agents.map((agent) => (
                        <option key={agent.id} value={agent.id}>
                          👤 {agent.name} - {agent.email} ({agent.status})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="ticket-detail__modal-actions">
                    <button 
                      className="ticket-detail__btn ticket-detail__btn--primary"
                      onClick={handleReassignTicket}
                      disabled={!reassignData.selectedAgentId}
                    >
                      Confirm Reassignment
                    </button>
                    <button 
                      className="ticket-detail__btn ticket-detail__btn--secondary"
                      onClick={toggleReassignModal}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="ticket-detail__tabs">
        <button
          className={`ticket-detail__tab ${uiState.activeTab === 'activity' ? 'ticket-detail__tab--active' : ''}`}
          onClick={() => setUiState(prev => ({ ...prev, activeTab: 'activity' }))}
        >
          📜 Activity & Details
        </button>
        <button
          className={`ticket-detail__tab ${uiState.activeTab === 'stats' ? 'ticket-detail__tab--active' : ''}`}
          onClick={() => setUiState(prev => ({ ...prev, activeTab: 'stats' }))}
        >
          📊 Stats & Tracking
        </button>
        <button
          className={`ticket-detail__tab ${uiState.activeTab === 'related' ? 'ticket-detail__tab--active' : ''}`}
          onClick={() => {
            setUiState(prev => ({ ...prev, activeTab: 'related' }));
            if (relatedTicketsData.tickets.length === 0) {
              loadRelatedTickets();
            }
          }}
        >
          📋 Related Tickets
        </button>
      </div>

      {/* Tab Content */}
      <div className="ticket-detail__content">
        {/* Activity Tab */}
        {uiState.activeTab === 'activity' && (
          <div className="ticket-detail__grid">
            <div className="ticket-detail__column ticket-detail__column--left">
              {/* Customer Query */}
              <div className="ticket-detail__card ticket-detail__card--highlight">
                <div className="ticket-detail__card-header">
                  <h3 className="ticket-detail__card-title">🎫 Customer Query</h3>
                </div>
                <p className="ticket-detail__query">{ticket.query}</p>
              </div>

              {/* Ticket Info */}
              <div className="ticket-detail__card">
                <div className="ticket-detail__card-header">
                  <h3 className="ticket-detail__card-title">ℹ️ Ticket Information</h3>
                </div>
                <dl className="ticket-detail__info-grid">
                  <div className="ticket-detail__info-item">
                    <dt className="ticket-detail__info-label">📁 Category</dt>
                    <dd className="ticket-detail__info-value">{ticket.category}</dd>
                  </div>
                  
                  <div className="ticket-detail__info-item">
                    <dt className="ticket-detail__info-label">👤 Assigned To</dt>
                    <dd className="ticket-detail__info-value">
                      {ticket.assigned_agent ? (
                        <span className="ticket-detail__assigned">
                          {ticket.assigned_agent.name}
                        </span>
                      ) : (
                        <span className="ticket-detail__unassigned">Not Assigned</span>
                      )}
                    </dd>
                  </div>
                  
                  <div className="ticket-detail__info-item">
                    <dt className="ticket-detail__info-label">📅 Created</dt>
                    <dd className="ticket-detail__info-value">
                      {formatDate(ticket.created_at)}
                    </dd>
                  </div>
                  
                  <div className="ticket-detail__info-item">
                    <dt className="ticket-detail__info-label">🔄 Last Updated</dt>
                    <dd className="ticket-detail__info-value">
                      {formatDate(ticket.updated_at)}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Update Form */}
              {canEdit && (
                <div className="ticket-detail__card">
                  <div className="ticket-detail__card-header">
                    <h3 className="ticket-detail__card-title">✏️ Update Ticket</h3>
                  </div>
                  
                  <form className="ticket-detail__update-form">
                    <div className="ticket-detail__form-row">
                      <div className="ticket-detail__form-group">
                        <label className="ticket-detail__label">Status</label>
                        <select
                          className="ticket-detail__select"
                          value={formData.status}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            status: e.target.value 
                          }))}
                        >
                          <option value="Open">Open</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Closed">Closed</option>
                          <option value="Pending">Pending</option>
                        </select>
                      </div>

                      <div className="ticket-detail__form-group">
                        <label className="ticket-detail__label">Priority</label>
                        <select
                          className="ticket-detail__select"
                          value={formData.priority}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            priority: e.target.value 
                          }))}
                        >
                          <option value="Low">Low</option>
                          <option value="Medium">Medium</option>
                          <option value="High">High</option>
                          <option value="Urgent">Urgent</option>
                        </select>
                      </div>
                    </div>

                    <div className="ticket-detail__form-group">
                      <label className="ticket-detail__label">Resolution</label>
                      <textarea
                        className="ticket-detail__textarea"
                        value={formData.resolution}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          resolution: e.target.value 
                        }))}
                        rows="4"
                        placeholder="Describe the resolution in detail..."
                      />
                    </div>

                    <button
                      type="button"
                      className="ticket-detail__btn ticket-detail__btn--primary ticket-detail__btn--large"
                      onClick={handleUpdateTicket}
                      disabled={uiState.isUpdating}
                    >
                      {uiState.isUpdating ? (
                        <>
                          <span className="spinner spinner--small"></span> Updating...
                        </>
                      ) : (
                        <>✅ Update Ticket</>
                      )}
                    </button>
                  </form>
                </div>
              )}
            </div>

            <div className="ticket-detail__column ticket-detail__column--right">
              {/* Activity Timeline */}
              <div className="ticket-detail__card">
                <div className="ticket-detail__card-header">
                  <h3 className="ticket-detail__card-title">📜 Activity Timeline</h3>
                </div>
                
                {ticket.logs && ticket.logs.length > 0 ? (
                  <div className="ticket-detail__timeline">
                    {ticket.logs.map((log) => (
                      <article key={log.id} className="ticket-detail__timeline-item">
                        <div className="ticket-detail__timeline-marker"></div>
                        
                        <div className="ticket-detail__timeline-content">
                          <header className="ticket-detail__timeline-header">
                            <span className="ticket-detail__timeline-author">
                              {log.message_from === 'agent' && '👤'}
                              {log.message_from === 'team_lead' && '👨‍💼'}
                              {log.message_from === 'manager' && '👔'}
                              {log.message_from === 'user' && '👥'}
                              {' '}{log.message_from}
                            </span>
                            <time className="ticket-detail__timeline-time">
                              {formatDate(log.created_at)}
                            </time>
                          </header>
                          
                          <p className="ticket-detail__timeline-message">{log.message}</p>
                          
                          {log.attachment && (
                            <div className="ticket-detail__timeline-attachment">
                              <a 
                                href={`https://aibot.psmsofttech.com${log.attachment}`}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="ticket-detail__attachment-link"
                              >
                                📎 View Attachment
                              </a>
                            </div>
                          )}
                          
                          <span 
                            className="ticket-detail__timeline-status"
                            style={{ backgroundColor: getStatusColor(log.current_status) }}
                          >
                            {log.current_status}
                          </span>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="ticket-detail__empty-state">
                    <span className="ticket-detail__empty-icon">📭</span>
                    <p>No activity yet</p>
                  </div>
                )}
              </div>

              {/* Add Comment */}
              {canEdit && (
                <div className="ticket-detail__card ticket-detail__card--comment">
                  <div className="ticket-detail__card-header">
                    <h3 className="ticket-detail__card-title">💬 Add Comment</h3>
                  </div>
                  
                  <div className="ticket-detail__comment-form">
                    <textarea
                      className="ticket-detail__textarea"
                      value={commentData.message}
                      onChange={(e) => setCommentData(prev => ({ 
                        ...prev, 
                        message: e.target.value 
                      }))}
                      placeholder="Write your comment here..."
                      rows="4"
                    />
                    
                    <div className="ticket-detail__file-upload">
                      <label className="ticket-detail__file-label">
                        <span className="ticket-detail__file-icon">📎</span>
                        Attach File
                        <input
                          type="file"
                          className="ticket-detail__file-input"
                          onChange={handleFileSelect}
                          accept=".txt,.pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.xls,.xlsx,.zip,.rar"
                        />
                      </label>
                      
                      {commentData.file && (
                        <div className="ticket-detail__file-selected">
                          <span className="ticket-detail__file-name">
                            ✅ {commentData.file.name}
                          </span>
                          <button
                            className="ticket-detail__file-remove"
                            onClick={() => {
                              setCommentData(prev => ({ ...prev, file: null }));
                              document.querySelector('.ticket-detail__file-input').value = '';
                            }}
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>

                    <button
                      className="ticket-detail__btn ticket-detail__btn--primary ticket-detail__btn--large"
                      onClick={handleAddCommentSubmit}
                      disabled={!commentData.message.trim() || uiState.isUploadingFile}
                    >
                      {uiState.isUploadingFile ? (
                        <>
                          <span className="spinner spinner--small"></span> Uploading...
                        </>
                      ) : (
                        <>💬 Add Comment</>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats Tab */}
        {uiState.activeTab === 'stats' && (
          <div className="ticket-detail__stats-content">
            <div className="ticket-detail__stats-grid">
              <div className="ticket-detail__stat-card">
                <div className="ticket-detail__stat-card-icon">⏱️</div>
                <div className="ticket-detail__stat-card-content">
                  <h4>Time Open</h4>
                  <p className="ticket-detail__stat-card-value">{ticketStats.timeOpen}</p>
                  <span className="ticket-detail__stat-card-label">Since creation</span>
                </div>
              </div>
              
              <div className="ticket-detail__stat-card">
                <div className="ticket-detail__stat-card-icon">💬</div>
                <div className="ticket-detail__stat-card-content">
                  <h4>Total Responses</h4>
                  <p className="ticket-detail__stat-card-value">{ticketStats.totalResponses}</p>
                  <span className="ticket-detail__stat-card-label">Messages exchanged</span>
                </div>
              </div>
              
              <div className="ticket-detail__stat-card">
                <div className="ticket-detail__stat-card-icon">📎</div>
                <div className="ticket-detail__stat-card-content">
                  <h4>Attachments</h4>
                  <p className="ticket-detail__stat-card-value">{ticketStats.attachmentCount}</p>
                  <span className="ticket-detail__stat-card-label">Files uploaded</span>
                </div>
              </div>
              
              <div className="ticket-detail__stat-card">
                <div className="ticket-detail__stat-card-icon">🕐</div>
                <div className="ticket-detail__stat-card-content">
                  <h4>Last Activity</h4>
                  <p className="ticket-detail__stat-card-value">{ticketStats.lastActivity}</p>
                  <span className="ticket-detail__stat-card-label">Most recent update</span>
                </div>
              </div>
            </div>

            {/* Timeline Chart */}
            <div className="ticket-detail__card">
              <div className="ticket-detail__card-header">
                <h3 className="ticket-detail__card-title">📈 Activity Over Time</h3>
              </div>
              <div className="ticket-detail__timeline-chart">
                {ticket.logs && ticket.logs.map((log, index) => (
                  <div key={log.id} className="ticket-detail__timeline-bar">
                    <div className="ticket-detail__timeline-bar-label">
                      {formatDate(log.created_at).split(' ')[0]}
                    </div>
                    <div 
                      className="ticket-detail__timeline-bar-fill"
                      style={{ 
                        width: `${((index + 1) / ticket.logs.length) * 100}%`,
                        backgroundColor: getStatusColor(log.current_status)
                      }}
                    ></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Related Tab */}
        {uiState.activeTab === 'related' && (
          <div className="ticket-detail__related-content">
            {relatedTicketsData.isLoading ? (
              <div className="ticket-detail__loading">
                <div className="spinner"></div>
                <p>Loading related tickets...</p>
              </div>
            ) : relatedTicketsData.tickets.length > 0 ? (
              <div className="ticket-detail__related-grid">
                {relatedTicketsData.tickets.map((relatedTicket) => (
                  <article
                    key={relatedTicket.id}
                    className="ticket-detail__related-card"
                    onClick={() => navigate(`/ticket/${relatedTicket.id}`)}
                  >
                    <header className="ticket-detail__related-header">
                      <span className="ticket-detail__related-number">
                        {relatedTicket.ticket_number}
                      </span>
                      <span
                        className="ticket-detail__related-status"
                        style={{ backgroundColor: getStatusColor(relatedTicket.status) }}
                      >
                        {relatedTicket.status}
                      </span>
                    </header>
                    
                    <p className="ticket-detail__related-query">{relatedTicket.query}</p>
                    
                    {relatedTicket.resolution && (
                      <div className="ticket-detail__related-resolution">
                        <strong>✅ Resolution:</strong>
                        <p>{relatedTicket.resolution}</p>
                      </div>
                    )}
                    
                    <footer className="ticket-detail__related-footer">
                      <time>{relatedTicket.created_at}</time>
                      <span className="ticket-detail__related-category">
                        📁 {relatedTicket.category}
                      </span>
                    </footer>
                  </article>
                ))}
              </div>
            ) : (
              <div className="ticket-detail__empty-state">
                <span className="ticket-detail__empty-icon">📭</span>
                <h3>No Related Tickets</h3>
                <p>No other tickets found from the same company</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
